import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Scissors, 
  Zap, 
  Flame, 
  Shirt, 
  Box, 
  Wrench, 
  Scan, 
  GraduationCap, 
  Settings, 
  Truck,
  Mail
} from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import { formatPrice } from '../lib/currency';

export default function ServicesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const services = [
    {
      icon: <Scissors className="w-12 h-12" />,
      title: t('services.polystyrene.title'),
      description: t('services.polystyrene.description'),
      specs: t('services.polystyrene.specs'),
      materials: t('services.polystyrene.materials'),
      priceFrom: 300,
      priceTo: 500,
      priceUnit: "hodina",
      video: "https://www.youtube.com/embed/ldCGZWUhWUI",
      color: 'from-blue-500 to-blue-600',
      borderColor: 'border-blue-500/20 hover:border-blue-400/40'
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: t('services.laser.title'),
      description: t('services.laser.description'),
      specs: t('services.laser.specs'),
      materials: t('services.laser.materials'),
      priceFrom: 400,
      priceTo: 700,
      priceUnit: "hodina",
      video: "https://www.youtube.com/embed/iIz99YNneYM",
      color: 'from-red-500 to-red-600',
      borderColor: 'border-red-500/20 hover:border-red-400/40'
    },
    {
      icon: <Flame className="w-12 h-12" />,
      title: t('services.plasma.title'),
      description: t('services.plasma.description'),
      specs: t('services.plasma.specs'),
      materials: t('services.plasma.materials'),
      pricePerMeter: 30,
      pricePierce: 5,
      video: "https://www.youtube.com/embed/wKG1-hd5CZk",
      color: 'from-orange-500 to-orange-600',
      borderColor: 'border-orange-500/20 hover:border-orange-400/40'
    },
    {
      icon: <Shirt className="w-12 h-12" />,
      title: t('services.embroidery.title'),
      description: t('services.embroidery.description'),
      specs: t('services.embroidery.specs'),
      materials: t('services.embroidery.materials'),
      priceHour: 600,
      priceSetup: 200,
      priceUnit: "hodina + manipulační poplatek + materiál",
      video: "https://www.youtube.com/embed/8fr7qnsnk0I",
      color: 'from-purple-500 to-purple-600',
      borderColor: 'border-purple-500/20 hover:border-purple-400/40'
    },
    {
      icon: <Box className="w-12 h-12" />,
      title: t('services.printing3d.title'),
      description: t('services.printing3d.description'),
      specs: t('services.printing3d.specs'),
      materials: t('services.printing3d.materials'),
      priceFrom: 300,
      priceTo: 500,
      priceUnit: "hodina",
      video: "https://www.youtube.com/embed/Rm1RB6Kgodc",
      color: 'from-green-500 to-green-600',
      borderColor: 'border-green-500/20 hover:border-green-400/40'
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: t('services.welding.title'),
      description: t('services.welding.description'),
      specs: t('services.welding.specs'),
      materials: t('services.welding.materials'),
      priceFrom: 500,
      priceTo: 1700,
      priceUnit: "hodina",
      video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      color: 'from-yellow-500 to-yellow-600',
      borderColor: 'border-yellow-500/20 hover:border-yellow-400/40'
    },
    {
      icon: <Scan className="w-12 h-12" />,
      title: t('services.milling3d.title'),
      description: t('services.milling3d.description'),
      specs: t('services.milling3d.specs'),
      materials: t('services.milling3d.materials'),
      priceFrom: 500,
      priceTo: 1000,
      priceUnit: "hodina",
      video: "https://www.youtube.com/embed/Wvk6WlG7vTs",
      color: 'from-cyan-500 to-cyan-600',
      borderColor: 'border-cyan-500/20 hover:border-cyan-400/40'
    },
    {
      icon: <GraduationCap className="w-12 h-12" />,
      title: t('services.training.title'),
      description: t('services.training.description'),
      specs: t('services.training.specs'),
      materials: t('services.training.materials'),
      priceFrom: 800,
      priceTo: 3500,
      priceUnit: "hodina",
      video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      color: 'from-indigo-500 to-indigo-600',
      borderColor: 'border-indigo-500/20 hover:border-indigo-400/40'
    },
    {
      icon: <Settings className="w-12 h-12" />,
      title: t('services.service.title'),
      description: t('services.service.description'),
      specs: t('services.service.specs'),
      materials: t('services.service.materials'),
      priceKm: 8,
      priceFrom: 450,
      priceTo: 1000,
      priceUnit: "km + hodina práce",
      video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      color: 'from-pink-500 to-pink-600',
      borderColor: 'border-pink-500/20 hover:border-pink-400/40'
    },
    {
      icon: <Truck className="w-12 h-12" />,
      title: t('services.transport.title'),
      description: t('services.transport.description'),
      specs: t('services.transport.specs'),
      materials: t('services.transport.materials'),
      priceKm: 12,
      priceUnit: "km",
      video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      color: 'from-gray-500 to-gray-600',
      borderColor: 'border-gray-500/20 hover:border-gray-400/40'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20">
      <ParticleBackground />
      <section className="py-20 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center mb-6">
              <Settings className="w-16 h-16 text-[#EE1C23] mr-4" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron">
                {t('services.title')}
              </h1>
            </div>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('services.subtitle')}
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`bg-black/40 backdrop-blur-md border ${service.borderColor} rounded-2xl p-8 hover:scale-105 transition-all`}
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-lg flex items-center justify-center text-white`}>
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white font-audiowide">
                    {service.title}
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* YouTube Video Embed */}
                  <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                    <iframe
                      src={service.video}
                      title={service.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                  
                  {service.materials && (
                    <div>
                      <h4 className="text-white text-sm mb-2">{t('services.laser.materialsLabel')}</h4>
                      <p className="text-gray-400 text-sm">{service.materials}</p>
                    </div>
                  )}
                  
                  {service.specs && (
                    <div>
                      <h4 className="text-white text-sm mb-2">{t('services.laser.specsLabel')}</h4>
                      <p className="text-gray-400 text-sm">{service.specs}</p>
                    </div>
                  )}
                  
                  <div className="pt-4 border-t border-gray-700">
                    <h4 className="text-white text-sm mb-2">{t('services.laser.priceLabel')}</h4>
                    <div className="text-[#EE1C23] font-bold text-sm">
                      {service.priceFrom && service.priceTo ? (
                        <span>
                          {formatPrice(service.priceFrom, i18n.language)} - {formatPrice(service.priceTo, i18n.language)} / {service.priceUnit}
                        </span>
                      ) : service.pricePerMeter ? (
                        <div>
                          <div>{formatPrice(service.pricePerMeter, i18n.language)} / metr řezu</div>
                          <div>{formatPrice(service.pricePierce, i18n.language)} / propal</div>
                        </div>
                      ) : service.priceHour ? (
                        <span>
                          {formatPrice(service.priceHour, i18n.language)} / {service.priceUnit}
                        </span>
                      ) : service.priceKm ? (
                        <span>
                          {service.priceFrom && service.priceTo ? 
                            `${formatPrice(service.priceKm, i18n.language)} / ${service.priceUnit.split(' + ')[0]} + ${formatPrice(service.priceFrom, i18n.language)} - ${formatPrice(service.priceTo, i18n.language)} / ${service.priceUnit.split(' + ')[1]}` :
                            `${formatPrice(service.priceKm, i18n.language)} / ${service.priceUnit}`
                          }
                        </span>
                      ) : (
                        <span>Cena na dotaz</span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-20 text-center"
          >
            <div className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-8 max-w-2xl mx-auto">
              <h2 className="text-2xl text-white mb-6 font-orbitron">
                {t('services.contactForServices')}
              </h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  navigate('/contact');
                  window.scrollTo(0, 0);
                }}
                className="bg-gradient-to-r from-[#EE1C23] to-red-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all flex items-center space-x-3 mx-auto"
              >
                <Mail className="w-5 h-5" />
                <span>
                  {t('contact.contactUs')}
                </span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}